import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Link } from 'react-router-dom';
import { AppState } from '../../../../store/store.types';

import { deskEditRoles, DeskGroupModel, DeskListFilters, DeskModel } from '../../../../api/core/core.types';
import Select from '../../../../components/forms/Select';
import { DeskActionTypes } from '../../types/desk.types';
import { getDeskList, updateDeskFilters } from '../../actions/desk.actions';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import { getDeskGroupList } from '../../actions/deskgroup.actions';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { convertArrayToQueryParameterArray } from '../../../../helpers/parameter.formatter';
import { WithTranslation, withTranslation } from 'react-i18next';
import StatusLabel from '../../../../components/forms/StatusLabel';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface DeskProps extends WithTranslation, RouteComponentProps {
  getDeskList: (filters?: DeskListFilters, page?: PageRequest) => void;
  getDeskGroupList: () => void;
  updateFilters: (filters: DeskListFilters) => void;
  desks: DeskModel[];
  user: UserModel | null;
  deskGroups: DeskGroupModel[];
  totalPages: number;
  totalElements: number;
  filters: DeskListFilters | null;
}

interface DeskState {
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  filtersUpdated: boolean;
  filter: {
    combinedFieldFilter: {
      value: string;
    };
    deskGroupFilter: {
      value: { label: string; value: string }[];
    };
    statusFilter: {
      value: { label: string; value: string }[];
    };
  };
}

const DeskSortFields = [
  { value: 'code', label: 'CODE' }
];

class Desks extends React.Component<DeskProps, DeskState> {
  constructor(props: DeskProps) {
    super(props);
    this.state = {
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'code',
      filtersUpdated: false,
      filter: {
        combinedFieldFilter: {
          value: ''
        },
        deskGroupFilter: {
          value: []
        },
        statusFilter: {
          value: []
        }
      }
    };
  }

  componentDidMount() {
    this.updateFilterValues();
    this.props.getDeskGroupList();
    this.props.getDeskList(this.props.filters ?? undefined);
  }

  componentDidUpdate() {
    if (!this.state.filtersUpdated) {
      this.updateFilterValues();
    }
  }

  updateFilterValues = () => {
    this.setState({ filtersUpdated: true });
    const filters = this.props.filters;
    if (filters) {
      const deskGroups = filters.groups ? filters.groups.split(',') : undefined;

      const selectedGroupOptions: { value: string; label: string }[] = [];
      if (deskGroups?.length) {
        deskGroups.forEach((dg) => {
          const group = this.props.deskGroups.find((d) => d.id === Number(dg));
          if (group) selectedGroupOptions.push({ value: String(group.id), label: group.name });
        });
      }

      const selectedStatusOptions: { value: string; label: string }[] = [];
      if (filters.status === 'ACTIVE') selectedStatusOptions.push({ value: 'ACTIVE', label: 'ACTIVE' });
      else if (filters.status === 'INACTIVE') selectedStatusOptions.push({ value: 'INACTIVE', label: 'INACTIVE' });

      this.setState({
        selectedPageNumber: filters.pageNumber ?? 1,
        filtersUpdated: true,
        filter: {
          combinedFieldFilter: {
            value: filters.combinedFieldFilter ?? ''
          },
          deskGroupFilter: {
            value: selectedGroupOptions
          },
          statusFilter: {
            value: selectedStatusOptions
          }
        }
      });
    }
  };

  clearFilters = async () => {
    this.setState({
      selectedPageNumber: 1,
      filtersUpdated: true,
      filter: {
        combinedFieldFilter: {
          value: ''
        },
        deskGroupFilter: {
          value: []
        },
        statusFilter: {
          value: []
        }
      }
    });

    const filters = this.composeDeskFilters();
    this.props.updateFilters(filters);
    this.props.getDeskList();
  };

  onRowClick = (rowInfo: any) => (
    this.props.history.push(`/setup/desks/${rowInfo.original.code}/view`)
  );

  mandatoryItemCell = (props: any) => {
    return (<div>{props.length}</div>);
  };

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    const filters = this.composeDeskFilters();
    this.props.getDeskList(filters);
    this.props.updateFilters(filters);
  };

  composeDeskFilters = () => {
    const status: any = this.state.filter.statusFilter.value;

    const groups: any[] = [];
    this.state.filter.deskGroupFilter.value.forEach((v) => {
      groups.push(v.value);
    });

    const filters: DeskListFilters = {
      combinedFieldFilter: this.state.filter.combinedFieldFilter.value,
      status: status.value,
      groups: convertArrayToQueryParameterArray(groups)
    };

    return filters;
  };

  getDeskGroupOptions = () => {
    const deskGroups = this.props.deskGroups;
    const deskGroupOptions: { value: string; label: string }[] = [];

    if (deskGroups) {
      deskGroups.forEach((dg) => {
        deskGroupOptions.push({
          label: dg.name,
          value: String(dg.id)
        });
      });
    }

    return deskGroupOptions;
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  dropdownChange = (newValue: any, field: string, isMulti: boolean) => {
    this.setState({
      ...this.state,
      filter: {
        ...this.state.filter,
        [ field ]: {
          value: newValue
        }
      }
    }, () => {
      if (!isMulti) {
        this.handleUpdateResults();
      }
    });
  };

  handleUpdateResults = () => {
    const filters = this.composeDeskFilters();
    this.props.getDeskList(filters);
    this.props.updateFilters(filters);
  };

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = DeskSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: columnName });
    return (
      this.props.getDeskList(this.composeDeskFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      this.props.getDeskList(this.composeDeskFilters(), pageRequest);

      this.setState({
        selectedPageNumber: pageRequest.page + 1,
        sortOrder: pageRequest.sortDirection,
        sortedBy: pageRequest.sortField
      });
    }
  }

  statusCell = (props: any) => (<StatusLabel status={props}/>);

  defineColumns() {
    const { t } = this.props;

    return [
      {
        header: t('ct.desks_list.code'),
        accessorKey: 'code',
        id: 'code'
      },
      { header: t('ct.desks_list.group'), accessorKey: 'group.name', id: 'group.name' },
      { header: t('ct.desks_list.name'), accessorKey: 'name', id: 'name' },
      { header: t('ct.desks_list.city'), accessorKey: 'address.city', id: 'address.city' },
      {
        header: t('ct.desks_list.mandatory_items'),
        accessorKey: 'itemPrices',
        id: 'itemPrices',
        cell: (info: any) => this.mandatoryItemCell(info.getValue())
      },
      {
        header: t('ct.desks_list.status'),
        accessorKey: 'statusType',
        cell: (info: any) => this.statusCell(info.getValue())
      }
    ];
  }

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>Desks</h1>
            {this.props.user && userCanAccessComponent(this.props.user, deskEditRoles) && (
              <Link
                to={{
                  pathname: '/setup/desks/create'
                }}
                className="btn btn-primary btn-wide"
              >
                {t('b.add_new').toUpperCase()}
              </Link>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex flex-wrap filters-container">
              <input
                value={this.state.filter.combinedFieldFilter.value}
                type="text"
                name="combinedFieldFilter"
                className="form-control col edit-form-input margins pl-1 ml-1 min-width-form-input filter-item-margin"
                placeholder={t('ph.filter.desk_by_name_Platceholder for desks list filter by name and code')}
                onChange={this.changeHandler}
              />
              <Select
                value={this.state.filter.deskGroupFilter.value}
                className="mr-1 filter-item-margin"
                placeholder={t('ph.filter.desk_group')}
                options={this.getDeskGroupOptions()}
                onChange={(val) => this.dropdownChange(val, 'deskGroupFilter', true)}
                closeMenuOnSelect={false}
                isMulti
                elWidth={350}
                equalWidthElement
                hideSelectedOptions={false}
                handleBlurChanged={() => {
                  this.handleUpdateResults();
                }}
                label="Desk group"
              />
              <Select
                value={this.state.filter.statusFilter.value}
                className="mr-1"
                placeholder={t('ph.filter.by_status')}
                options={[
                  { value: 'ACTIVE', label: 'ACTIVE' },
                  { value: 'INACTIVE', label: 'INACTIVE' }
                ]}
                onChange={(val) => this.dropdownChange(val, 'statusFilter', false)}
                equalWidthElement
                marginTop
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                label="status"
              />
            </div>
            <div className="pl-1">
              <button type="button"
                      className="btn btn-text-only mr-2"
                      onClick={() => this.clearFilters()}
              >{t('b.clear_filter')}
              </button>
              <button type="submit"
                      className="btn btn-primary filter-button"
                      onClick={this.onFilterClick}
              >{t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          {this.props.desks ? (
            <div className="table-list">
              <ListViewTable
                data={this.props.desks}
                columns={this.defineColumns()}
                getTrProps={this.onRowClick}
                sortBy={this.sortBy}
                sortOrder={this.state.sortOrder}
                sortedBy={this.state.sortedBy}
                paginationEnabled={true}
                totalPages={this.props.totalPages}
                totalElements={this.props.totalElements}
                selectedPageNumber={this.state.selectedPageNumber}
                getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
              />
            </div>
          ) : null}
        </div>
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  desks: state.setup.desks.desks,
  deskGroups: state.setup.deskGroups.deskGroups,
  user: state.authentication.user,
  totalPages: state.setup.desks.totalPages,
  totalElements: state.setup.desks.totalElements,
  filters: state.setup.desks.filters
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, DeskActionTypes>) => ({
  getDeskList: (filters?: DeskListFilters, page?: PageRequest) => dispatch(getDeskList(filters, page)),
  getDeskGroupList: () => dispatch(getDeskGroupList()),
  updateFilters: (filters: DeskListFilters) => dispatch(updateDeskFilters(filters))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(Desks));
